import type { FC, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

import { CoxBridgeBar, RenderWhenOktaEnabled } from '@makemydeal/dr-shared-components';
import { DOCKED_PANEL_TOP_VAR } from '@makemydeal/dr-activities-recent-deals';
import * as dealValidationSelectors from '@makemydeal/dr-dash-store/selectors/dealValidationSelectors';
import * as routingSelectors from '@makemydeal/dr-dash-store/selectors/router';
import { RouteValues } from '@makemydeal/dr-dash-store/routeConstants';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useResizeObserver, useRoleAssumptionSelector } from '@makemydeal/dr-shared-ui-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';

import { BridgeBarContainer, HeaderContainer, HeaderRoot, TopBarContainer } from './Header.style.interstate';
import { useHeaderMeasure } from './HeaderMeasureProvider';
import ContentHeader from './ContentHeader';

export interface HeaderInterstateProps {
    appPrefix: string;
    viewAsBanner?: ReactNode;
    assistantEntry?: ReactNode;
}

export const HeaderInterstate: FC<HeaderInterstateProps> = (props) => {
    const theme = useInterstateTheme();
    const [sliderHeight, setSliderHeight] = useState<number>(0);
    const isMvDealValidationEnabled = useSelector(featureToggleSelectors.isMvDealValidationEnabled);
    const dealValidation = useSelector(dealValidationSelectors.getDealValidationState);
    const currentRoute = useSelector(routingSelectors.getRouterPathName);
    const isDealSaveInProgress = useSelector((state: StateTree) => state.openDeal?.isDealSaveInProgress || false);
    const { isRoleAssumptionStarted } = useRoleAssumptionSelector();

    const refRoot = useRef<HTMLDivElement | null>(null);
    const refSlider = useRef<HTMLDivElement | null>(null);
    const refTopBar = useRef<HTMLDivElement | null>(null);
    const { setHeight } = useHeaderMeasure();

    // Check if the deal validation is enabled and valid
    const isDealValidOrValidationDisabled = !isMvDealValidationEnabled || (isMvDealValidationEnabled && dealValidation?.valid);
    const isRoleAssumptionRoute = currentRoute === RouteValues.ROLE_ASSUMPTION;

    useResizeObserver(refRoot, (entry) => {
        const totalHeaderHeight = entry.borderBoxSize[0].blockSize;
        setHeight(totalHeaderHeight - sliderHeight);
    });
    useResizeObserver(refSlider, (entry) => {
        setSliderHeight(entry.borderBoxSize[0].blockSize);
    });
    useResizeObserver(refTopBar, (entry) => {
        document.documentElement.style.setProperty(DOCKED_PANEL_TOP_VAR, `${entry.borderBoxSize[0].blockSize}px`);
    });

    useEffect(() => {
        return () => {
            document.documentElement.style.removeProperty(DOCKED_PANEL_TOP_VAR);
        };
    }, []);

    return (
        <HeaderRoot data-testid="header-root" ref={refRoot} showBorder={isDealValidOrValidationDisabled} theme={theme}>
            <HeaderContainer>
                <TopBarContainer ref={refTopBar}>
                    <BridgeBarContainer>
                        <RenderWhenOktaEnabled>
                            <CoxBridgeBar appPrefix={props.appPrefix} />
                        </RenderWhenOktaEnabled>
                    </BridgeBarContainer>
                </TopBarContainer>
                {isRoleAssumptionStarted && !isDealSaveInProgress && props.viewAsBanner}
                {isDealValidOrValidationDisabled && !isRoleAssumptionRoute && (
                    <ContentHeader assistantEntry={props.assistantEntry} />
                )}
            </HeaderContainer>
        </HeaderRoot>
    );
};

export default HeaderInterstate;
